import {
  ClipboardCheckIcon,
  CollectionIcon,
  DocumentTextIcon,
  MicrophoneIcon,
  PresentationChartBarIcon,
  VideoCameraIcon,
} from '@heroicons/react/outline';
import { AdminCourseLmsStatus } from '@shared/apis/courses.apis';
import { mergeClassNames } from 'apps/agora/src/utils/helpers';
import { ReactNode } from 'react';

export const LMS_BASE_URL = 'https://lms.upgrade.education';

export const LMS_STATUS_STYLES: Record<
  AdminCourseLmsStatus,
  { label: string; className: string; description: string }
> = {
  linked: {
    label: 'LMS content ready',
    className: 'bg-customGreen/15 text-customGreen',
    description: 'Linked to an LMS course that has learning units.',
  },
  empty: {
    label: 'LMS course is empty',
    className: 'bg-customRed/15 text-customRed',
    description: 'Linked to an LMS course that has no learning units yet.',
  },
  missing: {
    label: 'LMS course not found',
    className: 'bg-customRed/15 text-customRed',
    description: 'The linked LMS course id no longer exists in the LMS.',
  },
  unlinked: {
    label: 'No LMS course',
    className: 'bg-white/10 text-customGrey',
    description: 'Students of this course get no LMS content.',
  },
  checking: {
    label: 'Checking LMS…',
    className: 'bg-white/10 text-customGrey',
    description: 'LMS content is being checked in the background.',
  },
  unavailable: {
    label: 'LMS unreachable',
    className: 'bg-customGold/15 text-customGold',
    description: 'The LMS did not answer, so content could not be checked.',
  },
};

export const LmsStatusBadge = (props: { status: AdminCourseLmsStatus; className?: string }) => {
  const style = LMS_STATUS_STYLES[props.status];
  return (
    <span
      title={style.description}
      className={mergeClassNames(
        'inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold',
        style.className,
        props.className
      )}
    >
      {style.label}
    </span>
  );
};

export const FlagPill = (props: { children: ReactNode; tone?: 'neutral' | 'good' | 'warn' }) => (
  <span
    className={mergeClassNames(
      'inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium',
      props.tone === 'good' && 'bg-customPrimary/15 text-customPrimary',
      props.tone === 'warn' && 'bg-customGold/15 text-customGold',
      (!props.tone || props.tone === 'neutral') && 'bg-white/10 text-customGrey'
    )}
  >
    {props.children}
  </span>
);

export const CourseSwatch = (props: {
  color?: string;
  abbreviation?: string;
  size?: 'small' | 'large';
}) => (
  <span
    style={{ backgroundColor: props.color || '#4b5563' }}
    className={mergeClassNames(
      'flex shrink-0 items-center justify-center rounded-xl font-bold uppercase text-black',
      props.size === 'large' ? 'h-14 w-14 text-base' : 'h-9 w-9 text-xs'
    )}
  >
    {(props.abbreviation || '?').slice(0, 4)}
  </span>
);

export const LMS_ACCESS_LABELS: Record<string, string> = {
  free: 'Free',
  paid: 'Paid',
  draft: 'Draft',
  private: 'Private',
  coming_soon: 'Coming soon',
  enrollment_closed: 'Enrollment closed',
};

export const lmsAccessLabel = (access?: string) =>
  access ? LMS_ACCESS_LABELS[access] ?? access.replace(/_/g, ' ') : 'Unknown';

export const unitTypeIcon = (type: string) => {
  const className = 'h-4 w-4 shrink-0';
  if (/video|youtube|vimeo|wistia/.test(type)) return <VideoCameraIcon className={className} />;
  if (/audio|podcast/.test(type)) return <MicrophoneIcon className={className} />;
  if (/ebook|pdf|text|html|document|scorm/.test(type))
    return <DocumentTextIcon className={className} />;
  if (/assessment|exam|quiz|test|form|survey/.test(type))
    return <ClipboardCheckIcon className={className} />;
  if (/live|webinar|zoom|meeting|session/.test(type))
    return <PresentationChartBarIcon className={className} />;
  return <CollectionIcon className={className} />;
};

export const formatUnitTypes = (unitsByType?: Record<string, number>) =>
  unitsByType
    ? Object.entries(unitsByType)
        .sort((a, b) => b[1] - a[1])
        .map(([type, count]) => `${count} ${type.replace(/_/g, ' ')}`)
        .join(' · ')
    : '';

export const formatUnitDuration = (seconds?: number) => {
  if (!seconds || seconds <= 0) return '';
  const minutes = Math.round(seconds / 60);
  if (minutes < 1) return `${Math.round(seconds)}s`;
  if (minutes < 60) return `${minutes} min`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
};

export const COUNTRY_OPTIONS = [
  { label: 'No targeted country', value: '' },
  { label: 'US', value: 'US' },
  { label: 'UK', value: 'UK' },
  { label: 'EU', value: 'EU' },
];
